import type { ReactNode } from 'react';
import { cx } from './cx';
import type { IconName } from './Icon';

export interface EmptyStateProps {
  /** Accepted for compatibility. The empty state no longer draws an icon. */
  icon?: IconName;
  /** A short line in the sans at 600: "No funders yet." Leave it out and the body stands alone. */
  title?: ReactNode;
  /** One or two sentences at 400 in ink-2: what would put something here, not just that there is nothing. */
  body?: ReactNode;
  action?: ReactNode;
  /** Compact fits inside a card or a register body. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * A short Instrument Sans 600 line, a sentence or two at regular weight,
 * one button, left-aligned, inside a dashed box: a space waiting to be
 * filled rather than a panel that failed to render. No icon in a circle.
 *
 * Only the title is bold. The empty states used to arrive as a whole
 * paragraph in the title slot, so every empty list in the product was a
 * block of bold text.
 */
export function EmptyState({ title, body, action, size = 'md', className }: EmptyStateProps) {
  return (
    <div
      className={cx(
        'flex flex-col items-start rounded-ct-md border border-dashed border-line-strong bg-paper text-left',
        size === 'sm' ? 'gap-2 px-4 py-5' : 'gap-3 px-5 py-8',
        className,
      )}
    >
      {title ? <p className="text-sm font-semibold text-ink">{title}</p> : null}
      {body ? <div className="max-w-[60ch] text-sm text-ink-2">{body}</div> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
